import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, DANGER, SUCCESS } from "const"
import { Lifecycle } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    LifecycleImportFilters,

    LoadAllLifecycles,
    SortLifecycles,
    GetMaxLifecycles,

    setLifecycleImportFromLibraryList,
    setLifecycleImportFromLibraryIds,
    setLifecycleImportFromLibrarySort,
    setLifecycleImportFromLibraryFilters,

    setLifecycleImportFromAccountList,
    setLifecycleImportFromAccountIds,
    setLifecycleImportFromAccountSort,
    setLifecycleImportFromAccountFilters,

    setLifecycleReloadList,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    PageForm,
    Feedback,
    Loader,
    TableSearchWrapper,
    Table,
    SortRows,
} from "client/components"

import LifecycleImportSearch from "client/pages/search/lifecycleImportSearch"
import Columns from "./columns"

const ACCOUNT = "#account"
const LIBRARY = "#library"

const ImportLifecycleList = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const reloadImported = useSelector((state: StoreType) => state.lifecycle.reloadImported)

    const libraryLifecycleList = useSelector((state: StoreType) => state.lifecycle.importFromLibrary.lifecycleList)
    const libraryLifecycleIds = useSelector((state: StoreType) => state.lifecycle.importFromLibrary.lifecycleIds)
    const librarySortColumn = useSelector((state: StoreType) => state.lifecycle.importFromLibrary.sortColumn)
    const librarySortOrder = useSelector((state: StoreType) => state.lifecycle.importFromLibrary.sortOrder)
    const libraryFilters = useSelector((state: StoreType) => state.lifecycle.importFromLibrary.filters)

    const accountLifecycleList = useSelector((state: StoreType) => state.lifecycle.importFromAccount.lifecycleList)
    const accountLifecycleIds = useSelector((state: StoreType) => state.lifecycle.importFromAccount.lifecycleIds)
    const accountSortColumn = useSelector((state: StoreType) => state.lifecycle.importFromAccount.sortColumn)
    const accountSortOrder = useSelector((state: StoreType) => state.lifecycle.importFromAccount.sortOrder)
    const accountFilters = useSelector((state: StoreType) => state.lifecycle.importFromAccount.filters)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [accountLifecyclesNumber, setAccountLifecyclesNumber] = useState(0)
    const [importedFromLibrary, setImportedFromLibrary] = useState<number[]>([])
    const [importedFromAccount, setImportedFromAccount] = useState<number[]>([])
    const [activeTab, setActiveTab] = useState(ACCOUNT)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    // on component unmount
    useEffect(() => (() => {
        dispatch(setLifecycleReloadList(false))
        setTimeout(() => {
            dispatch(setLifecycleReloadList(true))
        }, 0)
    }), [])

    useEffect(() => {
        if (!reloadImported) return

        setLoadState(PENDING)
        const promiseArray: Promise<any>[] = []
        const importedLibrary: number[] = []
        const importedAccount: number[] = []

        promiseArray.push(
            // account lifecycles
            LoadAllLifecycles(userData.accountId, null).then((res) => {
                Object.values(res.lifecycleList).forEach((lifecycle: Lifecycle) => {
                    if (lifecycle.originalLifecycleId) importedLibrary.push(lifecycle.originalLifecycleId)
                })
                setImportedFromLibrary(importedLibrary)
                setAccountLifecyclesNumber(res.lifecycleIds.length)
            }),
            // ready for import from Super Admin
            LoadAllLifecycles(1, null).then((res) => {
                dispatch(setLifecycleImportFromLibraryList(res.lifecycleList))
            }),
        )

        if (project.id) {
            promiseArray.push(
                // project lifecycles
                LoadAllLifecycles(userData.accountId, project.id).then((res) => {
                    Object.values(res.lifecycleList).forEach((lifecycle: Lifecycle) => {
                        if (lifecycle.originalLifecycleId) importedAccount.push(lifecycle.originalLifecycleId)
                    })
                    setImportedFromAccount(importedAccount)
                }),
                // ready for import from account
                LoadAllLifecycles(userData.accountId, null).then((res) => {
                    dispatch(setLifecycleImportFromAccountList(res.lifecycleList))
                }),
            )
        }

        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadImported])

    useEffect(() => {
        const sortedIds = SortLifecycles(Object.values(libraryLifecycleList), librarySortColumn, librarySortOrder, libraryFilters)
        dispatch(setLifecycleImportFromLibraryIds(sortedIds))
    }, [libraryLifecycleList, librarySortColumn, librarySortOrder, libraryFilters])

    useEffect(() => {
        const sortedIds = SortLifecycles(Object.values(accountLifecycleList), accountSortColumn, accountSortOrder, accountFilters)
        dispatch(setLifecycleImportFromAccountIds(sortedIds))
    }, [accountLifecycleList, accountSortColumn, accountSortOrder, accountFilters])

    const onSortImportFromLibrary: SortRows = (column: keyof Lifecycle, order) => {
        dispatch(setLifecycleImportFromLibrarySort(column, order))
    }

    const onSortImportFromAccount: SortRows = (column: keyof Lifecycle, order) => {
        dispatch(setLifecycleImportFromAccountSort(column, order))
    }

    const onFilterImportFromLibrary = (newFilters: LifecycleImportFilters) => {
        dispatch(setLifecycleImportFromLibraryFilters(newFilters))
    }

    const onFilterImportFromAccount = (newFilters: LifecycleImportFilters) => {
        dispatch(setLifecycleImportFromAccountFilters(newFilters))
    }

    // max number of lifecycles
    const { maxLifecycles, feedback } = GetMaxLifecycles(packageSettings, userData, accountLifecyclesNumber, lang)

    const maxLifecyclesExceeded = maxLifecycles !== null && accountLifecyclesNumber > maxLifecycles


    const importFromLibraryColumns = Columns(importedFromLibrary, "library", accountLifecyclesNumber, maxLifecycles)
    const importFromLibrarySearch = (
        <TableSearchWrapper>
            <LifecycleImportSearch
                id="importFromLibrary"
                filters={libraryFilters}
                onFilter={onFilterImportFromLibrary}
            />
        </TableSearchWrapper>
    )
    const importFromLibraryTable = (
        <Table
            columns={importFromLibraryColumns}
            dataList={libraryLifecycleList}
            dataIds={libraryLifecycleIds}
            sortColumn={librarySortColumn}
            sortOrder={librarySortOrder}
            rowSize="md"
            showPagination
            onSort={onSortImportFromLibrary}
        />
    )
    const importFromLibraryFeedback = (
        <PageForm>
            <Feedback variant={maxLifecyclesExceeded ? DANGER : SUCCESS}>{feedback}</Feedback>
        </PageForm>
    )

    const importFromAccountColumns = Columns(importedFromAccount, "account", accountLifecyclesNumber, null)
    const importFromAccountSearch = (
        <TableSearchWrapper>
            <LifecycleImportSearch
                id="importFromAccount"
                filters={accountFilters}
                onFilter={onFilterImportFromAccount}
            />
        </TableSearchWrapper>
    )
    const importFromAccountTable = (
        <Table
            columns={importFromAccountColumns}
            dataList={accountLifecycleList}
            dataIds={accountLifecycleIds}
            sortColumn={accountSortColumn}
            sortOrder={accountSortOrder}
            rowSize="md"
            showPagination
            onSort={onSortImportFromAccount}
        />
    )

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.importLifecycles}</PageTitle>
            </PageHeader>
            {
                !project.id && (
                    <PageContent>
                        { loadState === PENDING && <Loader /> }
                        { importFromLibrarySearch }
                        { importFromLibraryTable }
                        { importFromLibraryFeedback }
                    </PageContent>
                )
            }
            {
                project.id && (
                    <PageContent>
                        <PageMenu>
                            <PageMenuButton
                                isActive={activeTab === ACCOUNT}
                                onClick={() => setActiveTab(ACCOUNT)}
                            >
                                {lang.account}
                            </PageMenuButton>
                            <PageMenuButton
                                isActive={activeTab === LIBRARY}
                                onClick={() => setActiveTab(LIBRARY)}
                            >
                                {lang.library}
                            </PageMenuButton>
                        </PageMenu>
                        <Tab show={activeTab === ACCOUNT}>
                            { loadState === PENDING && <Loader /> }
                            { importFromAccountSearch }
                            { importFromAccountTable }
                        </Tab>
                        <Tab show={activeTab === LIBRARY}>
                            { loadState === PENDING && <Loader /> }
                            { importFromLibrarySearch }
                            { importFromLibraryTable }
                            { importFromLibraryFeedback }
                        </Tab>
                    </PageContent>
                )
            }
        </Container>
    )
}
export default ImportLifecycleList
